// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * RESEARCH §9, as data: the things nobody has established. Each gets literal UI copy.
 * **Never a green check, never a red X.**
 *
 * Every `UNKNOWN` cell in `./matrix.ts` must be covered by an entry here — `tests/targets.test.ts`
 * fails the build otherwise. That is the mechanism that stops an `UNKNOWN` from being quietly
 * upgraded to a guess: to change a cell you must either produce evidence or write down what is
 * missing.
 *
 * `literal: true` means `uiCopy` is the sentence RESEARCH §9 says the interface must show, copied
 * verbatim. `literal: false` means it was written here for a gap §9 names without scripting copy for.
 */

import type { TargetId } from '../types.ts';

export interface UnresolvedPoint {
  readonly id: string;
  /** The RESEARCH §9 point this comes from, e.g. '9.3'. '1' means an unresolved cell in §1's matrix. */
  readonly section: string;
  /** A single keyword, when the point is about exactly one. */
  readonly keyword?: string;
  /** Several keywords, when the point names a set. Coverage is per keyword, never blanket. */
  readonly keywords?: readonly string[];
  readonly targets: readonly TargetId[];
  /**
   * 'keyword' — scoped to `keyword`/`keywords` on `targets`.
   * 'target'  — the provider is silent about this whole target; covers any keyword on it.
   * 'global'  — a caveat about the tool itself. Never counts as coverage for a matrix cell.
   */
  readonly scope: 'keyword' | 'target' | 'global';
  /** Shown in the UI, verbatim. */
  readonly uiCopy: string;
  readonly literal: boolean;
  /** The argument behind the point, for the details panel. */
  readonly detail?: string;
}

const OPENAI_ENFORCING: readonly TargetId[] = ['openai-strict', 'openai-nonstrict-responses'];
const OPENAI_ALL: readonly TargetId[] = [
  'openai-strict',
  'openai-nonstrict-responses',
  'openai-nonstrict-chat',
];
const ANTHROPIC_ALL: readonly TargetId[] = ['anthropic-strict', 'anthropic-nonstrict', 'anthropic-sdk'];
const GEMINI_ALL: readonly TargetId[] = ['gemini-parameters', 'gemini-jsonschema', 'vertex-parameters'];

export const UNRESOLVED: readonly UnresolvedPoint[] = [
  {
    id: 'openai-min-max-length',
    section: '9.1',
    keywords: ['minLength', 'maxLength'],
    targets: OPENAI_ENFORCING,
    scope: 'keyword',
    literal: true,
    uiCopy:
      '`minLength`/`maxLength` are absent from OpenAI\'s supported-properties list, and OpenAI\'s documentation is loose here. The only capture of a rejection comes from an AI-authored pull request. We could not verify this with a live request. Treat as unsupported and re-check before relying on it.',
    detail:
      'The two verifiers of the openai-strict topic directly contradict each other. Verifier A: resolved, treat as unsupported — they are absent from the "Supported string properties" whitelist, and the fine-tuned carve-out that names them also names patternProperties, which nobody claims is legal for base models. Verifier B: unresolved — the only capture is machine-authored (footer "Generated with mux • Model: anthropic:claude-opus-4-5") and its own "properties stripped" list includes nine keywords OpenAI documents as supported, so it is demonstrably over-broad. Warn; do not silently strip.',
  },
  {
    id: 'anthropic-unique-items',
    section: '9.2',
    keyword: 'uniqueItems',
    targets: ANTHROPIC_ALL,
    scope: 'keyword',
    literal: false,
    uiCopy:
      'On Anthropic, `uniqueItems` is genuinely unresolved — it appears on neither the supported nor the unsupported list. On OpenAI it ships as rejected, on the strength of one human-filed capture of the literal 400, with a named dissent.',
    detail:
      'Verifier A supplied a human-filed 2026 capture with the literal string, whose repro schema also carried minItems:1 and maxItems:50 on the same property UNFLAGGED — direct live confirmation that min/maxItems are accepted. Verifier B, attacking an evidence-free version of the claim, reported zero evidence in either direction. The two are not symmetric: one supplies a citation, the other reports its absence.',
  },
  {
    id: 'anthropic-documented-silence',
    section: '9.3',
    targets: ANTHROPIC_ALL,
    scope: 'target',
    literal: true,
    uiCopy: 'Anthropic documents neither support nor rejection for this keyword.',
    detail:
      'Anthropic\'s actual unsupported list is exactly seven bullets — recursive schemas · complex types within enums · external $ref · numerical constraints · string constraints · array constraints beyond minItems 0 or 1 · additionalProperties set to anything other than false. The commonly quoted longer list is a misquotation. oneOf, not, if/then/else, dependentRequired, dependentSchemas, patternProperties, propertyNames, minProperties, maxProperties, unevaluatedProperties, unevaluatedItems, contains, minContains and maxContains appear on NEITHER list.',
  },
  {
    id: 'gemini-parameters-json-schema-keywords',
    section: '9.4',
    targets: ['gemini-jsonschema'],
    scope: 'target',
    literal: true,
    uiCopy:
      'Google publishes no supported-keyword list for `parametersJsonSchema`. What follows is expected from a sibling field\'s documentation and is unverified — do not rely on `$ref` surviving here without testing.',
    detail:
      'The field\'s own description says only that it describes the parameters in JSON Schema format, must describe an object, and is mutually exclusive with `parameters`, with one example containing additionalProperties/required/propertyOrdering. The richer $id/$defs/$ref/$anchor/prefixItems/oneOf-as-anyOf list — including "cyclic references unrolled to a limited degree… only within non-required properties" and "if $ref is set on a sub-schema, no other properties except those starting with $ may be set" — belongs to GenerationConfig\'s response JSON-schema field, marked deprecated:true. Related discovery-doc mess, to cite carefully and never emit as a wire field: GenerationConfig.responseSchema and GenerationConfig._responseJsonSchema are both deprecated:true, and the live responseJsonSchema carries only the self-referential placeholder "An internal detail. Use responseJsonSchema rather than this field."',
  },
  {
    id: 'gemini-type-required',
    section: '9.5',
    keyword: 'type',
    targets: ['gemini-parameters', 'vertex-parameters'],
    scope: 'keyword',
    literal: true,
    uiCopy: 'Emit a type where you have one; warn, don\'t error, when a node lacks one.',
    detail:
      'The Gemini discovery doc says "Required. Data type." — but proto3 has no required fields, the enum\'s first member is TYPE_UNSPECIFIED, and the canonical union node ({"anyOf":[…]}) carries no parent type. Vertex\'s own discovery doc calls the same field "Optional. Data type of the schema field."',
  },
  {
    id: 'gemini-type-casing',
    section: '9.6',
    keyword: 'type',
    targets: ['gemini-parameters', 'vertex-parameters'],
    scope: 'keyword',
    literal: true,
    uiCopy:
      'Emit uppercase. Whether the REST endpoint also tolerates lowercase "string" was not testable without a key — do not claim either way.',
    detail:
      'The discovery document declares UPPERCASE proto enum names: STRING / NUMBER / INTEGER / BOOLEAN / ARRAY / OBJECT / NULL.',
  },
  {
    id: 'openai-pattern-dialect',
    section: '9.7',
    keyword: 'pattern',
    targets: OPENAI_ALL,
    scope: 'keyword',
    literal: true,
    uiCopy:
      'OpenAI does not document which regex flavour `pattern` uses. This construct may not survive constrained decoding.',
    detail:
      'structured-outputs.md says only "A regular expression that the string must match". The same docs site states that sampling is constrained by LLGuidance and that custom-tool grammar terminals use "the Rust regex crate syntax … not Python\'s re module", listing lookarounds in lexer regexes and lazy modifiers as unsupported. Rust\'s regex crate has no backreferences and no lookaround. A browser linter validating `pattern` with new RegExp() will green-light (?=…), (?<=…) and \\1 that plausibly fail server-side — and the GBNF path will fail too. This is the biggest single gap for a schema linter.',
  },
  {
    id: 'openai-prefix-items',
    section: '9.8',
    keyword: 'prefixItems',
    targets: OPENAI_ENFORCING,
    scope: 'keyword',
    literal: false,
    uiCopy:
      '`prefixItems` is absent from OpenAI\'s supported-properties whitelist and absent from Azure\'s unsupported table, and no live capture exists either way. We do not know whether it is accepted, ignored or rejected.',
  },
  {
    id: 'azure-documentation-divergence',
    section: '9.9',
    targets: OPENAI_ALL,
    scope: 'global',
    literal: true,
    uiCopy: 'Quote both texts; never transform.',
    detail:
      'Azure OpenAI is a documentation divergence, not an established API divergence. The page contradicts itself in adjacent paragraphs and carries ai-usage: ai-assisted. That is why there is no azure-openai target and no Azure-conservative transform: silently deleting a working constraint on the authority of one self-contradicting AI-assisted page is worse than warning.',
  },
  {
    id: 'openai-empty-parameters',
    section: '9.10',
    keyword: '#empty-parameters',
    targets: OPENAI_ALL,
    scope: 'keyword',
    literal: false,
    uiCopy:
      'Whether `parameters: {}` is accepted by OpenAI is untested. `null` is documented-legal on Responses and omission is documented-legal on Chat Completions; `{}` is what the Agents SDK defensively replaces, which is evidence about the SDK, not about the API.',
  },
  {
    id: 'nothing-reproduced-live',
    section: '9.11',
    targets: [
      'openai-strict',
      'openai-nonstrict-responses',
      'openai-nonstrict-chat',
      'anthropic-strict',
      'anthropic-nonstrict',
      'anthropic-sdk',
      'gemini-parameters',
      'gemini-jsonschema',
      'vertex-parameters',
      'gbnf-llamacpp',
      'gbnf-xgrammar',
    ],
    scope: 'global',
    literal: false,
    uiCopy:
      'Nothing in this matrix was reproduced against a live API. Every rule carries a provenance badge: DOC (quotable from the vendor), SDK (read from the vendor\'s own SDK source), SRC (llama.cpp/xgrammar/vLLM source) or FIELD (a third-party capture of a real 400). A FIELD string is never "we observed this".',
    detail:
      'Confidence labels in the upstream research bundles were inflated throughout — several claims marked "documented" rest entirely on third-party GitHub captures, and one rule whose own evidence says "undocumented" was still labelled "documented". This tool does not inherit that.',
  },
  {
    id: 'not-established-openai-format',
    section: '9.12',
    keyword: 'format',
    targets: OPENAI_ALL,
    scope: 'keyword',
    literal: false,
    uiCopy:
      'Whether an out-of-list `format` value (`uri`, `byte`, `int64`) errors or is ignored on OpenAI is not established. Nine values are documented as supported; the fate of a tenth is unwritten.',
  },
  {
    id: 'not-established-anthropic-root',
    section: '9.12',
    keyword: '#root-object',
    targets: ANTHROPIC_ALL,
    scope: 'keyword',
    literal: false,
    uiCopy:
      'Whether a non-object `input_schema` is rejected by Anthropic at runtime is not established. The type is pinned to the literal "object" in the docs; no capture of the failure exists.',
  },
  {
    id: 'not-established-gbnf-bare-root-ref',
    section: '9.12',
    keyword: '$ref#root',
    targets: ['gbnf-llamacpp', 'gbnf-xgrammar'],
    scope: 'keyword',
    literal: false,
    uiCopy:
      'Whether GBNF accepts a bare {"$ref": "#"} is not established. llama.cpp handles "#/…"; a bare "#" is not covered by any read of the source.',
  },
  {
    id: 'not-established-nonstrict-tool-counts',
    section: '9.12',
    keyword: '#tool-count',
    targets: ['anthropic-nonstrict', ...GEMINI_ALL],
    scope: 'keyword',
    literal: false,
    uiCopy:
      'No maximum non-strict tool count or total schema size is published for Anthropic or Gemini. No published number exists for either — a linter that invents one is asserting a number that does not exist.',
  },

  // ── Unresolved cells in §1's matrix that §9 does not script copy for ────────────────────────────
  {
    id: 'openai-annotation-keywords',
    section: '1',
    keywords: [
      '$dynamicRef',
      '$dynamicAnchor',
      '$schema',
      'title',
      'examples',
      'readOnly',
      'writeOnly',
      'deprecated',
      '$comment',
      '$id',
      '$anchor',
      'required#ghost',
    ],
    targets: OPENAI_ENFORCING,
    scope: 'keyword',
    literal: false,
    uiCopy:
      'OpenAI\'s supported-properties list names neither support nor rejection for this keyword, and no capture exists either way. It may be accepted and ignored, or it may 400 — we do not know.',
  },
  {
    id: 'vertex-ref-edge-cases',
    section: '1',
    keywords: ['$ref#root', '$ref#external', '$ref#siblings', '$dynamicRef', '$dynamicAnchor', 'required#ghost'],
    targets: ['vertex-parameters'],
    scope: 'keyword',
    literal: false,
    uiCopy:
      'Vertex accepts `ref` and `defs` as fields, but publishes nothing about self-reference, remote targets or sibling keys alongside a ref. Do not assume the Gemini API\'s behaviour transfers — the two documents already disagree about whether `type` is required.',
  },
  {
    id: 'ghost-required-entry',
    section: '1',
    keyword: 'required#ghost',
    targets: ['gemini-parameters', 'gemini-jsonschema'],
    scope: 'keyword',
    literal: false,
    uiCopy:
      'No provider documents what happens when `required` names a key that is absent from `properties`. llama.cpp silently drops the ghost entry; everyone else is unwritten.',
  },
  {
    id: 'anthropic-sdk-strip-list-not-exhaustive',
    section: '1',
    keywords: ['exclusiveMinimum', 'exclusiveMaximum', 'maxItems'],
    targets: ['anthropic-sdk'],
    scope: 'keyword',
    literal: false,
    uiCopy:
      'The Anthropic SDK\'s documented strip list is introduced with "for example" and names only `minimum`, `maximum`, `minLength` and `maxLength`. Whether it also strips this keyword for you — or lets the 400 through — is undocumented.',
  },
];

const isGlobal = (p: UnresolvedPoint): boolean => p.scope === 'global';

/**
 * Does an entry cover this (keyword, target) cell? Global caveats never count — they apply to
 * everything, so allowing them as coverage would make the check vacuous.
 */
export function coversCell(point: UnresolvedPoint, keyword: string, target: TargetId): boolean {
  if (isGlobal(point)) return false;
  if (!point.targets.includes(target)) return false;
  if (point.scope === 'target') return true;
  if (point.keyword === keyword) return true;
  return point.keywords?.includes(keyword) ?? false;
}

export function unresolvedFor(keyword: string, target: TargetId): readonly UnresolvedPoint[] {
  return UNRESOLVED.filter((p) => coversCell(p, keyword, target));
}

/** The caveats that apply to the whole tool, not to any one cell. */
export const GLOBAL_CAVEATS: readonly UnresolvedPoint[] = UNRESOLVED.filter(isGlobal);

/** The RESEARCH §9 sections this file covers, for the build check. */
export const COVERED_SECTIONS: readonly string[] = [
  ...new Set(UNRESOLVED.map((p) => p.section).filter((s) => s !== '1')),
];
